import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon, layout } from '@repo/ui';
import { useMe } from '../auth/session';
import { navFor } from './nav';

export function isActive(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

/** Role-filtered navigation (264px open, 68px collapsed) — design `nav` block. */
export function SideNav({ open, onNavigate }: { open: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const me = useMe();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <Box
      component="nav"
      aria-label={t('shell.menu')}
      sx={{
        width: open ? layout.navWidth : layout.navWidthCollapsed,
        flexShrink: 0,
        height: '100%',
        bgcolor: 'background.paper',
        borderInlineEnd: 1,
        borderColor: 'divider',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        overflowY: 'auto',
        boxSizing: 'border-box',
        transition: 'width .2s',
      }}
    >
      {navFor(me).map((g) => (
        <Box key={g.key} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {open && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'text.tertiary',
                px: 1.5,
                pt: 0.75,
                pb: 0.5,
              }}
            >
              {t(`nav.groups.${g.key}`)}
            </Box>
          )}
          {g.items.map((it) => {
            const active = isActive(pathname, it.to);
            const label = t(`nav.items.${it.key}`);
            const button = (
              <ButtonBase
                key={it.key}
                onClick={() => {
                  void navigate({ to: it.to });
                  onNavigate?.();
                }}
                aria-current={active ? 'page' : undefined}
                aria-label={open ? undefined : label}
                sx={(th) => ({
                  height: 36,
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  px: 1.5,
                  width: '100%',
                  fontSize: 14,
                  textAlign: 'start',
                  bgcolor: active ? th.vars.palette.primary.container : 'transparent',
                  color: active ? th.vars.palette.primary.onContainer : th.vars.palette.text.secondary,
                  fontWeight: active ? 600 : 500,
                  '&:hover': {
                    bgcolor: active ? th.vars.palette.primary.container : th.vars.palette.surface.container,
                  },
                })}
              >
                <Icon name={it.icon} />
                {open && (
                  <Box
                    component="span"
                    sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {label}
                  </Box>
                )}
              </ButtonBase>
            );
            return open ? (
              button
            ) : (
              <Tooltip key={it.key} title={label} placement="right">
                {button}
              </Tooltip>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
